import type { RefObject } from "react";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { PageTitle } from "@/components/ui/page-title";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import type { UnderlineTabOption } from "@/components/ui/underline-tabs";
import type { FieldState } from "@/domains/buildings/types/buildingWidget";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

export type StateFilter = "all" | FieldState;

function updatedLineText(lastUpdated: string | null, tw: BuildingDataWidgetSections): string {
  if (!lastUpdated) return tw.updatedNever;
  try {
    return tw.updatedAgo.replace(
      "{time}",
      formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })
    );
  } catch {
    return tw.updatedNever;
  }
}

function CompletionRing({
  completed,
  total,
  label,
}: {
  completed: number;
  total: number;
  label: string;
}) {
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  const radius = 23;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div
      className="relative h-[58px] w-[58px] shrink-0"
      aria-label={label.replace("{percent}", String(percent))}
    >
      <svg className="h-full w-full -rotate-90" viewBox="0 0 58 58" aria-hidden="true">
        <circle className="stroke-muted" cx="29" cy="29" r={radius} fill="none" strokeWidth="5" />
        <circle
          cx="29"
          cy="29"
          r={radius}
          fill="none"
          className="stroke-success"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-foreground">
        {percent}%
      </span>
    </div>
  );
}

function countWhenLoaded(isError: boolean, count: number | undefined): number | undefined {
  return isError ? undefined : count;
}

export interface BuildingDataPageHeaderProps {
  title: string;
  tw: BuildingDataWidgetSections;
  isError: boolean;
  totalCount: number | undefined;
  confirmedCount: number | undefined;
  pendingCount: number | undefined;
  emptyCount: number | undefined;
  lastUpdated: string | null;
  activeFilter: StateFilter;
  query: string;
  reviewCount: number;
  canManage: boolean;
  historyTriggerRef: RefObject<HTMLButtonElement>;
  onSearchInputMount: (element: HTMLInputElement | null) => void;
  onRetry: () => void;
  onFilterChange: (filter: StateFilter) => void;
  onQueryChange: (query: string) => void;
  onOpenReview: () => void;
  onOpenHistory: () => void;
}

function headerTabOptions(
  props: BuildingDataPageHeaderProps
): Array<UnderlineTabOption<StateFilter>> {
  return [
    {
      value: "all",
      label: props.tw.filterAll,
      count: countWhenLoaded(props.isError, props.totalCount),
    },
    {
      value: "confirmed",
      label: props.tw.statusConfirmed,
      count: countWhenLoaded(props.isError, props.confirmedCount),
    },
    {
      value: "pending",
      label: props.tw.statusNeedsReview,
      count: countWhenLoaded(props.isError, props.pendingCount),
    },
    {
      value: "empty",
      label: props.tw.statusMissing,
      count: countWhenLoaded(props.isError, props.emptyCount),
    },
  ];
}

/**
 * The ring is the header's only remaining summary: the four filter tabs below
 * already carry All / Confirmed / Needs review / Missing with their counts, so
 * repeating them as stat tiles said the same thing twice. The breakdown lives
 * in the ring's tooltip for the reader who wants the numbers without leaving
 * the current filter.
 */
function CompletionOverview(props: BuildingDataPageHeaderProps) {
  const breakdown: Array<{ label: string; count: number }> = [
    { label: props.tw.statusConfirmed, count: props.confirmedCount ?? 0 },
    { label: props.tw.statusNeedsReview, count: props.pendingCount ?? 0 },
    { label: props.tw.statusMissing, count: props.emptyCount ?? 0 },
  ];
  return (
    <Tooltip>
      {/* asChild would hand the trigger to the ring's plain div, which takes no
          focus — the counts have to stay reachable by keyboard, so the trigger
          renders its own button. */}
      <TooltipTrigger
        type="button"
        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <CompletionRing
          completed={props.confirmedCount ?? 0}
          total={props.totalCount ?? 0}
          label={props.tw.completionPercent}
        />
      </TooltipTrigger>
      <TooltipContent side="bottom" className="px-3 py-2">
        <dl className="flex flex-col gap-1">
          {breakdown.map((entry) => (
            <div key={entry.label} className="flex items-baseline justify-between gap-4">
              <dt className="text-xs text-muted-foreground">{entry.label}</dt>
              <dd className="text-xs font-semibold [font-variant-numeric:tabular-nums]">
                {entry.count}
              </dd>
            </div>
          ))}
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}

function ReviewFieldsButton(props: BuildingDataPageHeaderProps) {
  if (props.reviewCount === 0) return null;
  return (
    <Button
      className="h-10 px-4"
      disabled={!props.canManage}
      onClick={props.onOpenReview}
      {...pageInfoTarget("buildingData.review")}
    >
      {props.tw.reviewFields.replace("{count}", String(props.reviewCount))}
    </Button>
  );
}

function HeaderCounts(props: BuildingDataPageHeaderProps) {
  return (
    <>
      <CompletionOverview {...props} />
      <ReviewFieldsButton {...props} />
    </>
  );
}

function HeaderSummary(props: BuildingDataPageHeaderProps) {
  return (
    <div className="flex items-start gap-6 pb-4">
      <div className="min-w-0 flex-1">
        <PageTitle info="buildingData">{props.title}</PageTitle>
        <PageSubtitle className="mt-1">{props.tw.pageSubtitle}</PageSubtitle>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {props.isError ? (
          <Button variant="outline" onClick={props.onRetry}>
            <AlertTriangle size={15} className="text-destructive" />
            {props.tw.retryData}
          </Button>
        ) : (
          <HeaderCounts {...props} />
        )}
      </div>
    </div>
  );
}

function HeaderToolbar(props: BuildingDataPageHeaderProps) {
  const {
    activeFilter,
    lastUpdated,
    historyTriggerRef,
    onFilterChange,
    onOpenHistory,
    onQueryChange,
    onSearchInputMount,
    query,
    tw,
  } = props;
  return (
    <div className="flex min-h-11 items-end gap-3">
      <UnderlineTabs
        className="-mb-px min-w-0"
        value={activeFilter}
        onValueChange={onFilterChange}
        options={headerTabOptions(props)}
        infoTarget="buildingData.filter"
      />
      <div className="mb-2 ml-auto flex shrink-0 items-center gap-3">
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          {updatedLineText(lastUpdated, tw)}
        </span>
        <div className="relative w-60 shrink-0">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={onSearchInputMount}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={tw.searchFields}
            className="h-8 pl-8 text-xs"
            {...pageInfoTarget("buildingData.search")}
          />
        </div>
        <Button
          ref={historyTriggerRef}
          variant="ghost"
          size="controlIcon"
          className="text-muted-foreground hover:text-foreground"
          onClick={onOpenHistory}
          aria-label={tw.buildingDataHistory}
          {...pageInfoTarget("buildingData.history")}
          title={tw.buildingDataHistory}
        >
          <History />
        </Button>
      </div>
    </div>
  );
}

export function BuildingDataPageHeader(props: BuildingDataPageHeaderProps) {
  return (
    <header className="shrink-0 border-b border-border px-7 pt-4">
      <HeaderSummary {...props} />
      <HeaderToolbar {...props} />
    </header>
  );
}
